import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { OPACITY } from '../../../theme';
import { bridgingPlusRate, matchedText, onTrackRate, signed, sortLeastGrowthFirst } from '../growth';
import { GROWTH } from '../tokens';
import type { GrowthPeriod, GrowthRow, SchemeLevel, Skill } from '../types';
import { CardHead, GROWTH_CARD_SX, HEADER_CELL_SX, HEADER_ROW_SX, OpenChip, SMALL_SX, STRONG_SX, TEXT_SX } from './cardParts';
import { LevelBar, LevelLabel } from './LevelBar';

const GROWTH_WIDTH = 124;
const WORDS_WIDTH = 96;
const RATE_WIDTH = 136;
const ASSIGNMENTS_WIDTH = 96;
const CHEVRON_WIDTH = 16;

export interface GroupTableProps {
  title: string;
  sub: string;
  firstColumn: string;
  nameWidth?: number;
  rows: GrowthRow[];
  skill: Skill;
  period: GrowthPeriod;
  scheme: SchemeLevel[];
  onSelect: (row: GrowthRow) => void;
  showAssignments?: boolean;
  highlightLowest?: boolean;
  sortedNote?: boolean;
  stackedLabel?: boolean;
  hideAverage?: boolean;
  emptyText: string;
}

function RightCell({ width, children, testId, faded }: { width: number; children: React.ReactNode; testId?: string; faded?: boolean }) {
  return (
    <Box data-testid={testId} sx={{ flex: `0 0 ${width}px`, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} style={{ opacity: faded ? OPACITY.provisional : 1 }}>
      {children}
    </Box>
  );
}

function Separator() {
  return <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', bgcolor: GROWTH.divider, flexShrink: 0 }} />;
}

export function GroupTable({ title, sub, firstColumn, nameWidth = 200, rows, skill, period, scheme, onSelect, showAssignments, highlightLowest, sortedNote, stackedLabel, hideAverage, emptyText }: GroupTableProps) {
  const later = period.later === 'eoy' ? 'EOY' : 'MOY';
  const writing = skill === 'writing';
  const sorted = sortLeastGrowthFirst(rows, skill);
  const rates = sorted.map((row) => onTrackRate(row.skills[skill])).filter((rate): rate is number => rate !== null);
  const lowest = rates.length > 0 ? Math.min(...rates) : null;
  return (
    <Box component="section" data-testid="group-table" sx={{ ...GROWTH_CARD_SX, pb: '8px' }}>
      <CardHead
        title={title}
        sub={sub}
        legend={scheme}
        right={
          sortedNote ? (
            <Typography component="span" sx={{ ...TEXT_SX, color: GROWTH.muted, whiteSpace: 'nowrap' }}>
              Least growth first
            </Typography>
          ) : undefined
        }
      />
      <Box data-testid="group-header" sx={HEADER_ROW_SX}>
        <Box sx={{ ...HEADER_CELL_SX, flex: `0 0 ${nameWidth}px` }}>{firstColumn}</Box>
        <Box sx={{ ...HEADER_CELL_SX, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {`Levels at ${later} ${period.label}`}
          {period.open && <OpenChip />}
        </Box>
        <Box sx={{ ...HEADER_CELL_SX, flex: `0 0 ${GROWTH_WIDTH}px`, textAlign: 'right' }}>Growth</Box>
        {writing && <Box sx={{ ...HEADER_CELL_SX, flex: `0 0 ${WORDS_WIDTH}px`, textAlign: 'right' }}>Avg words</Box>}
        <Box sx={{ ...HEADER_CELL_SX, flex: `0 0 ${RATE_WIDTH}px`, textAlign: 'right' }}>{period.growthLabel}</Box>
        {showAssignments && (
          <>
            <Separator />
            <Box sx={{ ...HEADER_CELL_SX, flex: `0 0 ${ASSIGNMENTS_WIDTH}px`, textAlign: 'right' }}>Assignments</Box>
          </>
        )}
        <Box sx={{ width: CHEVRON_WIDTH, flexShrink: 0 }} />
      </Box>
      {sorted.length === 0 && (
        <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, py: '16px', m: 0 }}>
          {emptyText}
        </Typography>
      )}
      {sorted.map((row) => {
        const f = row.skills[skill];
        const rate = onTrackRate(f);
        const bridging = bridgingPlusRate(f);
        const hot = Boolean(highlightLowest) && rate !== null && rate === lowest;
        const rowSx = {
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          py: '12px',
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 0,
          borderBottom: `1px solid ${GROWTH.divider}`,
          px: 0,
          '&:last-of-type': { borderBottom: 0 },
        } as const;
        const cells = (
          <>
            <Box data-testid="group-name-cell" sx={{ flex: `0 0 ${nameWidth}px`, minWidth: 0 }}>
              <Typography component="span" data-testid="group-name" sx={{ display: 'block', ...STRONG_SX }}>
                {row.label}
              </Typography>
              <Typography component="span" sx={{ display: 'block', ...SMALL_SX, fontWeight: 400, whiteSpace: 'nowrap' }}>
                {row.sub}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {!hideAverage && <LevelLabel avg={f.avg} boyAvg={f.boyAvg} scheme={scheme} stacked={stackedLabel} />}
              <LevelBar levels={f.levels} scheme={scheme} faded={period.open} />
            </Box>
            <RightCell width={GROWTH_WIDTH} testId="growth-cell" faded={period.open}>
              <Typography component="b" sx={STRONG_SX}>
                {signed(f.growth)}
              </Typography>
              <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 400 }}>
                {matchedText(f, period.open)}
              </Typography>
            </RightCell>
            {writing && (
              <RightCell width={WORDS_WIDTH} testId="words-cell" faded={period.open}>
                <Typography component="span" sx={TEXT_SX}>
                  {f.words === null ? '—' : Math.round(f.words)}
                </Typography>
                <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 400 }}>
                  {f.boyWords === null ? '' : `BOY ${Math.round(f.boyWords)}`}
                </Typography>
              </RightCell>
            )}
            <RightCell width={RATE_WIDTH}>
              <Typography component="span" data-testid="on-track-rate" data-hot={hot ? 'true' : undefined} sx={{ ...TEXT_SX, fontWeight: hot ? 800 : 500, color: hot ? GROWTH.hot : rate === null ? GROWTH.disabled : GROWTH.ink }} style={{ opacity: period.open ? OPACITY.provisional : 1 }}>
                {rate === null ? '—' : `${rate}%`}
              </Typography>
              {bridging !== null && (
                <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 400, whiteSpace: 'nowrap' }}>
                  {`Bridging+ at BOY: ${bridging}%`}
                </Typography>
              )}
            </RightCell>
            {showAssignments && (
              <>
                <Separator />
                <RightCell width={ASSIGNMENTS_WIDTH} testId="assignments-cell">
                  <Typography component="span" sx={TEXT_SX}>
                    {row.assignments ?? 0}
                  </Typography>
                  <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 400 }}>
                    since BOY
                  </Typography>
                </RightCell>
              </>
            )}
            <Box sx={{ width: CHEVRON_WIDTH, flexShrink: 0, display: 'flex', color: GROWTH.muted }}>
              {row.drillable && <ChevronRightIcon aria-hidden="true" sx={{ fontSize: 16 }} />}
            </Box>
          </>
        );
        return row.drillable ? (
          <Box key={row.key} component="button" type="button" data-testid="group-row" onClick={() => onSelect(row)} sx={{ ...rowSx, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(8, 8, 27, 0.04)' } }}>
            {cells}
          </Box>
        ) : (
          <Box key={row.key} data-testid="group-row" sx={rowSx}>
            {cells}
          </Box>
        );
      })}
    </Box>
  );
}
